import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { adminApi } from "../api/admin";
import type { BlogPost, BlogPostRequest, BlogPostSummary } from "../api/types";
import { useAsync } from "../hooks/useAsync";
import { accession, formatDate } from "../lib/format";
import { AdminTable, type Column } from "./AdminTable";
import { AdminForm, type FieldDef, type FormValues } from "./AdminForm";
import type { AdminOutletContext } from "./AdminLayout";
import {
  isoToLocalInput,
  localInputToIso,
  parseTags,
  tagsToInput,
  toNullableString,
} from "./formUtils";

const FIELDS: FieldDef[] = [
  { name: "title", label: "Title", type: "text", required: true },
  { name: "slug", label: "Slug", type: "text", help: "Leave blank to derive from the title." },
  { name: "excerpt", label: "Excerpt", type: "textarea" },
  { name: "contentMarkdown", label: "Body (Markdown)", type: "textarea" },
  { name: "coverImageUrl", label: "Cover image", type: "upload", accept: "image/*",
    help: "Paste an image URL, or upload a file." },
  { name: "published", label: "Published", type: "checkbox" },
  { name: "publishedAt", label: "Published at", type: "datetime" },
  { name: "tags", label: "Tags", type: "tags", help: "Comma-separated, e.g. scuba, travel" },
];

function toForm(p?: BlogPost): FormValues {
  return {
    title: p?.title ?? "",
    slug: p?.slug ?? "",
    excerpt: p?.excerpt ?? "",
    contentMarkdown: p?.contentMarkdown ?? "",
    coverImageUrl: p?.coverImageUrl ?? "",
    published: p?.published ?? false,
    publishedAt: isoToLocalInput(p?.publishedAt),
    tags: tagsToInput(p?.tags),
  };
}

function toRequest(v: FormValues): BlogPostRequest {
  return {
    slug: toNullableString(v.slug),
    title: String(v.title ?? "").trim(),
    excerpt: toNullableString(v.excerpt),
    contentMarkdown: toNullableString(v.contentMarkdown),
    coverImageUrl: toNullableString(v.coverImageUrl),
    published: !!v.published,
    publishedAt: localInputToIso(v.publishedAt as string),
    tags: parseTags(v.tags),
  };
}

/** Re-publish/unpublish in place: fetch the full post, then PUT with the flag flipped. */
async function setPublished(id: number, value: boolean) {
  const full = await adminApi.getPost(id);
  await adminApi.updatePost(id, {
    slug: full.slug,
    title: full.title,
    excerpt: full.excerpt ?? null,
    contentMarkdown: full.contentMarkdown ?? null,
    coverImageUrl: full.coverImageUrl ?? null,
    published: value,
    publishedAt: full.publishedAt ?? null,
    tags: full.tags,
  });
}

export function AdminPosts() {
  const { refreshToken } = useOutletContext<AdminOutletContext>();
  const [reload, setReload] = useState(0);
  const { data, loading, error } = useAsync(() => adminApi.listPosts(), [refreshToken, reload]);
  const rows = data?.items ?? [];
  const [editing, setEditing] = useState<BlogPost | null>(null);
  const [creating, setCreating] = useState(false);
  const refetch = () => setReload((n) => n + 1);

  const columns: Column<BlogPostSummary>[] = [
    {
      key: "code",
      header: "Code",
      render: (p) => <span className="mono">{accession("BLOG_POST", p.id)}</span>,
      sortValue: (p) => p.id,
    },
    {
      key: "title",
      header: "Title",
      className: "cell-title",
      render: (p) => p.title,
      sortValue: (p) => p.title,
    },
    {
      key: "status",
      header: "Status",
      render: (p) =>
        p.published ? (
          <span className="pill pill--on">published</span>
        ) : (
          <span className="pill pill--off">draft</span>
        ),
      sortValue: (p) => (p.published ? 1 : 0),
    },
    {
      key: "publishedAt",
      header: "Published",
      render: (p) => <span className="mono">{formatDate(p.publishedAt) || "—"}</span>,
      sortValue: (p) => p.publishedAt ?? "",
    },
    {
      key: "tags",
      header: "Tags",
      render: (p) => <span className="cell-muted">{p.tags.join(", ") || "—"}</span>,
    },
  ];

  return (
    <>
      <AdminTable
        rows={rows}
        getId={(p) => p.id}
        columns={columns}
        searchText={(p) => `${p.title} ${p.slug} ${p.tags.join(" ")} ${p.excerpt ?? ""}`}
        searchPlaceholder="Filter posts…"
        typeFilter={{
          label: "Status",
          value: (p) => (p.published ? "PUB" : "DRAFT"),
          options: [
            { value: "PUB", label: "Published" },
            { value: "DRAFT", label: "Draft" },
          ],
        }}
        loading={loading}
        error={error ?? undefined}
        emptyLabel="No posts yet."
        createLabel="+ New post"
        onCreate={() => setCreating(true)}
        onEdit={async (p) => {
          const full = await adminApi.getPost(p.id);
          setEditing(full);
        }}
        onDelete={async (p) => {
          await adminApi.deletePost(p.id);
          refetch();
        }}
        bulkActions={[
          {
            label: "Publish selected",
            run: async (sel) => {
              for (const p of sel) if (!p.published) await setPublished(p.id, true);
              refetch();
            },
          },
          {
            label: "Unpublish selected",
            run: async (sel) => {
              for (const p of sel) if (p.published) await setPublished(p.id, false);
              refetch();
            },
          },
          {
            label: "Delete selected",
            danger: true,
            confirm: "Delete all selected posts?",
            run: async (sel) => {
              for (const p of sel) await adminApi.deletePost(p.id);
              refetch();
            },
          },
        ]}
      />
      {creating && (
        <AdminForm
          title="New post"
          fields={FIELDS}
          initial={toForm()}
          submitLabel="Create"
          onUpload={async (file) => (await adminApi.uploadFile(file)).url}
          onCancel={() => setCreating(false)}
          onSubmit={async (v) => {
            await adminApi.createPost(toRequest(v));
            setCreating(false);
            refetch();
          }}
        />
      )}
      {editing && (
        <AdminForm
          title={`Edit ${accession("BLOG_POST", editing.id)}`}
          fields={FIELDS}
          initial={toForm(editing)}
          onUpload={async (file) => (await adminApi.uploadFile(file)).url}
          onCancel={() => setEditing(null)}
          onSubmit={async (v) => {
            await adminApi.updatePost(editing.id, toRequest(v));
            setEditing(null);
            refetch();
          }}
        />
      )}
    </>
  );
}
